import { SerializedDockview } from 'dockview';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import {
  TabContext,
  TabHistories,
  TabHistoriesCursorDirection,
  TabHistory,
} from '@/entities/tab/model/tab.types';
import { atomFamily } from 'jotai-family';
import { atom } from 'jotai';
import {
  TAB_HISTORY_START_CURSOR,
  truncateHistoriesAfterCursor,
  limitHistories,
} from '@/entities/tab/model/tab';
import { TAB_HISTORY_MAX_COUNT } from '@/entities/tab/model/tab.config';

function createTabStorage() {
  const storage = typeof window !== 'undefined' ? sessionStorage : null;
  if (!storage) return { getItem: () => null, setItem: () => {}, removeItem: () => {} };

  return {
    getItem: (key: string) => storage.getItem(key),
    setItem: (key: string, value: string) => {
      try {
        storage.setItem(key, value);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'QuotaExceededError') {
          console.warn(`Storage quota exceeded for key: ${key}, clearing old data`);
          // 비교적 덜 중요한 탭 관련 데이터 삭제
          const keysToRemove: string[] = [];
          for (let i = 0; i < storage.length; i++) {
            const k = storage.key(i);
            if (k && (k.startsWith('tab-histories:') || k === '__HIDDEN_TABS__') && k !== key) {
              keysToRemove.push(k);
            }
          }
          keysToRemove.forEach((k) => storage.removeItem(k));
          try {
            storage.setItem(key, value);
          } catch {
            console.warn('Storage quota still exceeded after cleanup, skipping save');
          }
        } else {
          throw e;
        }
      }
    },
    removeItem: (key: string) => storage.removeItem(key),
  };
}

export const tabStateAtom = atomWithStorage<SerializedDockview | null>(
  '__TAB_STATE__',
  null,
  createJSONStorage(() => createTabStorage() as any)
);

/** 모드 전환 시 접근 불가로 숨긴 탭들. 해당 모드로 돌아오면 복원 */
export const hiddenTabsAtom = atomWithStorage<Required<TabContext>[]>(
  '__HIDDEN_TABS__',
  [],
  createJSONStorage(() => createTabStorage() as any)
);

export const tabHistoriesAtomFamily = atomFamily((tabId: string) =>
  atomWithStorage<TabHistories>(
    `tab-histories:${tabId}`,
    {
      tabId,
      histories: [],
      cursor: TAB_HISTORY_START_CURSOR,
    },
    createJSONStorage(createTabStorage)
  )
);

/** 현재 history entry의 params를 panel의 params로 갱신 (navigate 전에 호출) */
export const updateCurrentHistoryEntryParamsAtom = atom(
  null,
  (get, set, { tabId, params }: { tabId: string; params: Record<string, unknown> }) => {
    const targetAtom = tabHistoriesAtomFamily(tabId);
    const prev = get(targetAtom);
    const { cursor, histories } = prev;
    if (histories.length === 0) return;

    const currentEntry = histories[cursor];
    if (!currentEntry) return;

    const updatedHistories = [...histories];
    updatedHistories[cursor] = {
      ...currentEntry,
      context: {
        ...currentEntry.context,
        params: { ...currentEntry.context.params, ...params },
      },
    };

    set(targetAtom, { ...prev, histories: updatedHistories });
  }
);

export const appendTabHistoryAtom = atom(null, (get, set, context: Required<TabContext>) => {
  const targetAtom = tabHistoriesAtomFamily(context.tabId);
  const { cursor, histories } = get(targetAtom);

  const truncatedHistories = truncateHistoriesAfterCursor(histories, cursor);

  const lastCheckpoint = truncatedHistories[truncatedHistories.length - 1]?.checkpoint ?? -1;
  const nextIndex = lastCheckpoint + 1;

  const nextHistory: TabHistory = {
    checkpoint: nextIndex,
    context,
  };

  const newHistories = [...truncatedHistories, nextHistory];
  const limitedHistories = limitHistories(newHistories, TAB_HISTORY_MAX_COUNT);

  const adjustedCursor =
    limitedHistories.length < newHistories.length ? limitedHistories.length - 1 : nextIndex;

  set(targetAtom, {
    tabId: context.tabId,
    histories: limitedHistories,
    cursor: adjustedCursor,
  });
});

/**
 * 현재 항목을 새 context로 교체 (히스토리 추가 없음).
 * 위계 레벨이 낮아질 때(목적지 < 현재) 사용.
 */
export const replaceTabHistoryAtom = atom(null, (get, set, context: Required<TabContext>) => {
  const targetAtom = tabHistoriesAtomFamily(context.tabId);
  const prev = get(targetAtom);
  const { cursor, histories } = prev;
  if (histories.length === 0) return;

  const currentEntry = histories[cursor];
  if (!currentEntry) return;

  const nextHistory: TabHistory = {
    ...currentEntry,
    context,
  };

  const newHistories = [...histories];
  newHistories[cursor] = nextHistory;

  set(targetAtom, {
    ...prev,
    histories: newHistories,
  });
});

export const moveTabHistoryCursorAtom = atom(
  null,
  (get, set, { tabId, direction }: { tabId: string; direction: TabHistoriesCursorDirection }) => {
    const targetAtom = tabHistoriesAtomFamily(tabId);
    const prev = get(targetAtom);

    const { cursor, histories } = prev;
    if (histories.length === 0) return;

    const delta = direction === 'back' ? -1 : 1;

    const nextCursor = Math.min(Math.max(cursor + delta, 0), histories.length - 1);

    if (nextCursor === cursor) return;

    set(targetAtom, {
      ...prev,
      cursor: nextCursor,
    });
  }
);
